/**
 * Pure, framework-free payment-status polling controller.
 *
 * Extracted out of `thankyouV1/index.tsx` so the concurrency/abort
 * guards can be unit tested (see `paymentStatusPoller.test.ts`) without a
 * DOM or React renderer, matching the pattern used by
 * `features/imeiScanner/cameraSelection.ts`.
 */

export interface PaymentStatusResponse {
    status: number;
    data: string;
}

export interface PaymentStatusPollerCallbacks {
    /** Called for every non-stale `200` response with truthy `data`. */
    onStatus: (data: string) => void;
    /** Called for a real (non-cancelled) request error, unless already done. */
    onError: (error: unknown) => void;
    /**
     * Called once per attempt that actually completed a round trip
     * (success or a real error) — never for an attempt we cancelled
     * ourselves via `stop()`/abort.
     */
    onAttemptSettled: (attemptCount: number) => void;
    /** Called exactly once, the first time the poller stops for any reason. */
    onDone?: () => void;
}

export interface PaymentStatusPoller {
    /**
     * Runs a single poll attempt. No-ops (no network call) if a request
     * is already in flight, or the poller is already done.
     */
    poll: () => Promise<void>;
    /** Marks the poller done and aborts any in-flight request. Idempotent. */
    stop: () => void;
    isDone: () => boolean;
    getAttemptCount: () => number;
}

const FINAL_STATUSES = new Set(['success', 'failure']);

export function createPaymentStatusPoller(
    fetchStatus: (signal: AbortSignal) => Promise<PaymentStatusResponse>,
    callbacks: PaymentStatusPollerCallbacks,
    maxAttempts = 3
): PaymentStatusPoller {
    let isFetching = false;
    let stopped = false;
    let attemptCount = 0;
    const abortController = new AbortController();

    const stop = () => {
        if (stopped) return;
        stopped = true;
        abortController.abort();
        callbacks.onDone?.();
    };

    const poll = async () => {
        if (isFetching || stopped) return;
        isFetching = true;
        const { signal } = abortController;

        try {
            const response = await fetchStatus(signal);

            // This attempt completed a real round trip — count it even if
            // the answer turns out to be stale (see below), but not if it
            // was cancelled (handled in the catch branch instead).
            isFetching = false;
            attemptCount += 1;
            callbacks.onAttemptSettled(attemptCount);

            // The poller may have been stopped (e.g. another mechanism
            // already reached a final status) while this request was
            // in flight — ignore this now-stale response instead of
            // overwriting already-resolved state.
            if (stopped) return;

            if (response.status === 200 && response.data) {
                callbacks.onStatus(response.data);
                if (FINAL_STATUSES.has(response.data)) {
                    stop();
                    return;
                }
            }

            if (attemptCount >= maxAttempts) {
                stop();
            }
        } catch (err) {
            isFetching = false;

            if (signal.aborted) {
                // Deliberately cancelled mid-flight (final status reached
                // elsewhere, or the caller stopped polling) — not a
                // completed attempt, so it must not count towards
                // attemptCount or surface as an error.
                return;
            }

            attemptCount += 1;
            callbacks.onAttemptSettled(attemptCount);

            if (!stopped) {
                callbacks.onError(err);
            }

            if (attemptCount >= maxAttempts) {
                stop();
            }
        }
    };

    return {
        poll,
        stop,
        isDone: () => stopped,
        getAttemptCount: () => attemptCount,
    };
}
